import { type DtoEmbedded, type DtoOrder, DtoOrderOrderTypeEnum } from '@repo/api-types/order-retrieval';
import { isObject } from '@repo/utils/type.utils';

import { assetTypes } from './asset.types';
import type { PackageMeasurement } from './asset.types';

export const orderStatuses = {
    Booked: 'Booked',
    InProgress: 'In progress',
    Completed: 'Completed',
    HasAnIssue: 'Has an issue',
    NotFound: 'Not found',
} as const;

type OrderStatus = (typeof orderStatuses)[keyof typeof orderStatuses];

export const isOrderStatus = (orderStatus: unknown): orderStatus is OrderStatus => {
    return Object.values(orderStatuses).some((status) => status === orderStatus);
};

export function isOrder(value: unknown): value is OrderAsset {
    return isObject(value) && 'assetDetails' in value && value.assetType === assetTypes.order;
}

export function isOrderAssetDetails(value: unknown): value is OrderAssetDetails {
    return isObject(value) && 'retailItems' in value;
}

export function isSalesOrder(value: unknown): value is OrderAsset {
    return hasOrderType(value, DtoOrderOrderTypeEnum.SALESORDER);
}

export function isISOMOrder(value: unknown): value is OrderAsset {
    return hasOrderType(value, DtoOrderOrderTypeEnum.ISOM);
}

function hasOrderType(value: unknown, orderType: string): value is OrderAsset {
    return (
        isObject(value) &&
        'assetDetails' in value &&
        isObject(value.assetDetails) &&
        'orderType' in value.assetDetails &&
        value.assetDetails.orderType === orderType
    );
}

export interface OrderAsset {
    assetDetails: OrderAssetDetails;
    assetType: typeof assetTypes.order;
}

export interface OrderAssetDetails extends DtoOrder {
    customerMeetingPointId?: string; // TODO: add definition
    customerMeetingPointName?: string; // TODO: add definition
    _embedded?: DtoEmbedded & {
        selectedPickservice?: unknown; // TODO: add definition
    };
    retailItems?: RetailItem[];
}

export interface RetailItem {
    name: string;
    itemId: string;
    itemType: string;
    itemUnitCode: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription?: string;
    packageWeight: number;
    packageMeasurements?: PackageMeasurement[];
    paNumber: string;
    isReferenceItem?: boolean;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    price: RetailItemPrice;
    imageUrl: string;
    isAssemblyRequired: boolean;
    serviceProductRelations: ServiceProductRelation[];
    isKitchenUnit: boolean;
}

export interface RetailItemPrice {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: RetailItemTaxDetail[];
    type: string;
    indicativeTax: number;
}

export interface RetailItemTaxDetail {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface ServiceProductRelation {
    serviceProductId: string;
    itemType: string;
}
export const CancelOrderReasons = [
    'ITEMCHANGE',
    'CUSCHMIND',
    'CUST_BOUGHT_AT_STORE',
    'DELAY',
    'DOUBLE_ORDER',
    'FAULTY_GOODS_BY_DEL',
    'SAC_IN_SAMS',
    'STAT_47_NO_CUST_ANSWER',
    'TECHMALFUNC',
    'SALESERR',
] as const;

export type CancelOrderReasonCode = (typeof CancelOrderReasons)[number];
